import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryParam } from '../../../../shared/hooks/location'
import { useLoad } from '../../../../shared/hooks/load'
import { useApiListAuthProviderLink, useApiListProviderDomainStatus } from '../../../../api-new/state/auth'
import { copyFor, offersPassword } from './login-error-copy'
import { authPath, carryParams, domainOf } from '../hooks/auth-path'
import { fetchDomainStatus, passwordAllowedBy } from '../hooks/domain-status'
import { CopyValue } from '../../../../shared/sso-workspace/copy-value'
import { AuthConnection, AuthHead, AuthMark, AuthPage } from '../../../../shared/sso-workspace/auth-card'
import { isRequestID } from '../../../../shared/error/request-id'
import { REFERENCE_HINT, REFERENCE_LABEL } from '../../../../shared/error/error-reference'

// Friendly IdP error page (SSO hardening 4.7). The auth service redirects a
// rejected SSO sign-in here with ?reason=&code=&provider=<identifier> (2.10);
// this page never sees the raw IdP error description, only the mapped reason,
// the IdP's OAuth error code and -- when the description carried one -- the
// provider's own error number (?idp_code=, owner 2026-10-07: Microsoft's
// "AADSTS50020", parsed out server-side; never the description). The sign-in
// that failed also left the email and where to go after signing in
// (hooks/provider.js), so every way out keeps the person's place.
//
// The page names the connection (logo and name, from the public
// by-identifier lookup), says what happened in plain words, gives the one
// next step for that reason (login-error-copy.js), and the reference for
// support: the failed sign-in request's id (?ref=, owner 2026-10-06), which
// opens that request -- with its error -- in the Request log. The provider's
// error number, which IT admins search for, is a line of its own
// ("Microsoft's error code: AADSTS50020"); without one, that line shows the
// OAuth code. With one, the OAuth code is left out: it already chose the
// title and next step, the number says more, and the reference opens the
// whole error. A link from before ref existed (or one whose ref isn't a
// request id) keeps the old reference: reason, connection, IdP code and the
// time in UTC.
//
// Require SSO (owner 2026-10-06): when the reason's copy offers a password
// and the email's domain answers passwordAllowed: false, the server would
// refuse that password, so the page offers none and its next step doesn't
// mention one (login-error-copy.js). Until the domain status answers, the
// next step and the actions wait; a failed lookup offers the password as
// before.
//
// `providerLink` is an injection seam for tests. Real usage (mounted via the
// route below with no props) leaves it undefined, which is what triggers the
// by-identifier lookup; passing it explicitly -- including `null`, meaning
// "known, no help available" -- skips the fetch. `now` pins the reference
// time in tests.
export const LoginError = ({ providerLink: providerLinkProp, now }) => {
  const reason = useQueryParam('reason')
  const code = useQueryParam('code')
  const provider = useQueryParam('provider')
  const email = useQueryParam('email')
  const redirect = useQueryParam('redirect')
  const redirectDoc = useQueryParam('redirect-doc')
  const ref = useQueryParam('ref')
  const idpCodeParam = useQueryParam('idp_code')
  const idpCode = isErrorNumber(idpCodeParam) ? idpCodeParam : null

  const providerLinkGiven = providerLinkProp !== undefined

  // api-new hooks return a new object on every request-state change, so it
  // must never sit in a useLoad/useEffect deps array (infinite loop) -- read
  // it through a ref instead.
  const apiListAuthProviderLink = useApiListAuthProviderLink()
  const apiRef = useRef(apiListAuthProviderLink)
  apiRef.current = apiListAuthProviderLink

  const [fetchedProviderLink, updateFetchedProviderLink] = useState(null)

  // Ask the domain status only when the answer could change the page: an
  // email with a domain, and a reason whose copy offers a password.
  const domain = domainOf(email)
  const asksStatus = !!domain && offersPassword(reason, code)

  const apiListProviderDomainStatus = useApiListProviderDomainStatus()
  const statusRef = useRef(apiListProviderDomainStatus)
  statusRef.current = apiListProviderDomainStatus

  // null while the answer is on its way.
  const [passwordAllowed, updatePasswordAllowed] = useState(asksStatus ? null : true)

  // When this page opened: the moment the sign-in failed, near enough.
  const [at] = useState(() => now || new Date())

  useLoad(async () => {
    if (providerLinkGiven || !provider) {
      return
    }

    try {
      const link = await apiRef.current.get(`by-identifier/${encodeURIComponent(provider)}`)
      updateFetchedProviderLink(link || null)
    } catch {
      updateFetchedProviderLink(null)
    }
  }, [provider, providerLinkGiven])

  useLoad(async () => {
    if (!asksStatus) {
      updatePasswordAllowed(true)
      return
    }

    updatePasswordAllowed(null)
    updatePasswordAllowed(passwordAllowedBy(await fetchDomainStatus(statusRef.current, domain)))
  }, [domain, asksStatus])

  const providerLink = providerLinkGiven ? providerLinkProp : fetchedProviderLink

  const deciding = passwordAllowed === null

  const copy = copyFor(reason, code, providerLink && providerLink.displayName, { passwordAllowed: passwordAllowed !== false })

  const hasProviderHelp = copy.help && !!providerLink && !!(providerLink.helpText || providerLink.helpUrl)

  const carry = carryParams({ redirect, redirectDoc, email })

  const ACTIONS = {
    password: { text: 'Sign in with your password', to: authPath('/auth/email', { ...carry, use: 'password' }) },
    set_password: { text: 'Set a password', to: authPath('/auth/password-recover/request', { email }) },
    retry: { text: 'Try again', to: authPath('/auth/email', carry) },
    different_account: { text: 'Use a different account', to: authPath('/auth/email', { redirect, 'redirect-doc': redirectDoc }) }
  }

  // The connection's own "Request access" link, when it has one and this
  // reason is about access, goes first.
  const actions = [
    ...(hasProviderHelp && providerLink.helpUrl ? [{ key: 'request', text: 'Request access', href: providerLink.helpUrl }] : []),
    ...copy.actions.map(key => ({ key, ...ACTIONS[key] }))
  ]

  const reference = isRequestID(ref) ? ref : [reason || 'unknown', provider, code, formatUTC(at)].filter(Boolean).join(' · ')

  return (
    <AuthPage wide labelledBy="login-error-title">
      <AuthConnection id="login-error-provider" providerLink={providerLink} prefix="Signing in with" />

      <AuthHead id="login-error-title" title={copy.title} mark={<AuthMark tone={copy.tone}>!</AuthMark>} />

      <p className="m-0" id="login-error-body">{copy.body}</p>

      {!deciding &&
        <section className="sa-next" aria-labelledby="login-error-next-title">
          <h2 id="login-error-next-title">What you can do</h2>
          <p id="login-error-next">{copy.next}</p>
        </section>}

      {hasProviderHelp && providerLink.helpText &&
        <section className="sa-next" id="login-error-provider-help" aria-labelledby="login-error-help-title">
          <h2 id="login-error-help-title">From {providerLink.displayName || 'your organization'}</h2>
          <p className="sa-help" id="login-error-help-text">{providerLink.helpText}</p>
        </section>}

      {!deciding &&
        <div className="sa-actions">
          {actions.map((action, i) => {
            const className = `btn ${i === 0 ? 'sw-btn-primary' : 'sw-btn'} sa-block`
            return action.href
              ? <a key={action.key} id="login-error-request-access" className={className} href={action.href} target="_blank" rel="noopener noreferrer">{action.text}</a>
              : <Link key={action.key} id={`login-error-action-${action.key}`} className={className} to={action.to}>{action.text}</Link>
          })}
        </div>}

      <div className="sa-ref" id="login-error-code">
        {(idpCode || code) &&
          <p className="sa-idp-code" data-testid="login-error-idp-code">
            {errorCodeLabel(providerLink && providerLink.displayName)}: <span className="sw-mono">{idpCode || code}</span>
          </p>}
        <CopyValue id="login-error-reference" label={REFERENCE_LABEL} value={reference} hint={REFERENCE_HINT} />
      </div>

      <p className="sa-foot">
        <Link replace id="login-error-back" to={authPath('/auth/email', carry)}>Back to sign in</Link>
      </p>
    </AuthPage>
  )
}

// The provider's error number as the server sends it (callback-error.go):
// one short token of letters, digits, '_' and '-'. Anything else in the URL
// -- a hand-made link -- is not shown.
export const isErrorNumber = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,32}$/.test(value)

// "Microsoft's error code", "Acme Schools' error code": whose code the IdP's
// error is, for an IT admin who searches it.
export const errorCodeLabel = name => {
  if (!name) return "The sign-in provider's error code"
  return `${name}${/s$/i.test(name) ? "'" : "'s"} error code`
}

// "2026-09-26 14:03 UTC": minutes are enough to find the sign-in, and UTC is
// what the findings are stored in.
export const formatUTC = date => `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`
